import { defer, throwError } from "rxjs";
import { ajax, AjaxRequest, AjaxResponse } from "rxjs/ajax";
import { Observable } from "rxjs/internal/Observable";
import { catchError, map, retry } from "rxjs/operators";
import Utils from "utils/utils";

const SLOW_API_THRESHOLD_MS = 3000;

/** types */
type PartAjaxRequest = Omit<AjaxRequest, "url" | "method" | "body">;
type HttpMethod = "GET" | "POST" | "DELETE" | "PUT";
type HeadersAjax = {
    Authorization: string;
    Accept: string;
    "Content-Type": string;
    "Sec-Fetch-Site"?: string;
    "X-projectGUID"?: string;
};
interface Param {
    url: string;
    data?: any;
    headers?: PartAjaxRequest;
}

/** functions */
function mapResponse(res: AjaxResponse) {
    if (res.response) {
        return res.response;
    }
}

function handleError$(err: unknown): Observable<unknown> {
    return throwError(err);
}

function mapAjaxRequest(request?: PartAjaxRequest) {
    const token = Utils.getValueLocalStorage("token");
    const mapHeaders = request?.headers
        ? ({ ...request.headers } as HeadersAjax)
        : undefined;
    const customAuth = mapHeaders?.Authorization
        ? mapHeaders.Authorization
        : token
        ? `Bearer ${token}`
        : "";
    const newHeaders = {
        Authorization: customAuth,
        Accept: "application/json",
        "Content-Type": "application/json",
        ...mapHeaders,
    };
    return { ...request, headers: { ...newHeaders } };
}

function commonApiCall(method: HttpMethod, param: Param): Observable<unknown> {
    const { url, data, headers } = param;
    const newHeaders = mapAjaxRequest(headers);
    const projGUID = Utils.getProjectGUIDFromUrl(window.location.search);
    if (!newHeaders.headers["X-projectGUID"]) {
        newHeaders.headers["X-projectGUID"] = projGUID;
    }
    const body = data;
    return defer(() => {
        const startTime = performance.now();
        return ajax({ url, method, body, ...newHeaders }).pipe(
            map((res: AjaxResponse) => {
                const duration = performance.now() - startTime;
                if (duration > SLOW_API_THRESHOLD_MS) {
                    console.warn(
                        `[api-slow] ${method} ${url} took ${Math.round(
                            duration
                        )}ms`
                    );
                }
                return mapResponse(res);
            }),
            retry(1),
            catchError((err) => {
                const duration = performance.now() - startTime;
                console.error(
                    `[api-error] ${method} ${url} failed after ${Math.round(
                        duration
                    )}ms`,
                    err?.response ?? err
                );
                return handleError$(err);
            })
        );
    });
}

/** base class */
export default class HttpClient {
    static get(url: string, headers?: PartAjaxRequest): Observable<unknown> {
        return commonApiCall("GET", { url, headers });
    }

    static post(
        url: string,
        data: unknown,
        headers?: PartAjaxRequest
    ): Observable<unknown> {
        return commonApiCall("POST", { url, data, headers });
    }
    static put(
        url: string,
        data: unknown,
        headers?: PartAjaxRequest
    ): Observable<unknown> {
        return commonApiCall("PUT", { url, data, headers });
    }

    static delete(url: string, headers?: PartAjaxRequest): Observable<unknown> {
        return commonApiCall("DELETE", { url, headers });
    }
    static upload(
        url: string,
        data: unknown,
        headers?: PartAjaxRequest
    ): Observable<unknown> {
        const token = Utils.getValueLocalStorage("token");
        const projGUID = Utils.getProjectGUIDFromUrl(window.location.search);
        const newHeaders = {
            Authorization: token ? `Bearer ${token}` : "",
            Accept: "text/plain",
            "Sec-Fetch-Site": "same-origin",
            "X-projectGUID": projGUID,
        };
        return ajax({
            url,
            method: "POST",
            body: data,
            headers: { ...newHeaders },
        }).pipe(
            map((res) => mapResponse(res)),
            retry(1),
            catchError((err) => handleError$(err))
        );
    }
}
